/**
 * FeCollegeCompare — side-by-side compare view for 2-3 UK FE colleges
 * or specialist HE providers. Powers the mature-learner and career-
 * changer flow ("Blackpool vs New College Durham for my HND").
 *
 * Reads `?ids=uk-hesa-XXX,uk-hesa-YYY,uk-hesa-ZZZ` from the URL and
 * fetches each via `GET /api/universities/{id}` in parallel. No new
 * backend endpoint required — this is a client-side composition of
 * data we already surface on the individual uni pages.
 *
 * Deliberately narrow scope: Ofsted rating, institution type, city,
 * degree count, description. NO acceptance rate / salary comparisons
 * — FE colleges rarely have those fields populated and showing
 * "—" columns weakens the trust signal.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Loader2, XCircle, GraduationCap } from "lucide-react";
import OfstedChip from "@/components/app/OfstedChip";

const TYPE_LABELS = {
  further_education_college: "Further education college",
  further_education_college_group: "FE college group",
  university_centre: "University centre",
  specialist_conservatoire: "Conservatoire",
  conservatoire: "Conservatoire",
  specialist_he_provider: "Specialist HE provider",
  specialist_he_college: "Specialist HE college",
  private_he_provider: "Private HE provider",
};

export default function FeCollegeCompare() {
  const [params, setParams] = useSearchParams();
  const idsRaw = params.get("ids") || "";
  const ids = useMemo(
    () => idsRaw.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3),
    [idsRaw]
  );

  const [state, setState] = useState({ loading: true, unis: [], err: null });
  const [degreeCounts, setDegreeCounts] = useState({});

  useEffect(() => {
    if (!ids.length) {
      setState({ loading: false, unis: [], err: null });
      return;
    }
    setState({ loading: true, unis: [], err: null });
    Promise.all(
      ids.map((id) =>
        api
          .get(`/universities/${encodeURIComponent(id)}`)
          .then((r) => r.data)
          .catch(() => null)
      )
    ).then((results) => {
      const unis = results.filter(Boolean);
      setState({ loading: false, unis, err: null });
    });
    // Pull degree counts per uni — cheap parallel fetch.
    Promise.all(
      ids.map((id) =>
        api
          .get(`/degrees?university_id=${encodeURIComponent(id)}&limit=1`)
          .then(() =>
            api
              .get(`/universities/${encodeURIComponent(id)}/directory-courses?limit=1`)
              .then((r) => [id, r.headers?.["x-total-count"] || null])
              .catch(() => [id, null])
          )
          .catch(() => [id, null])
      )
    ).then((pairs) => {
      const map = {};
      for (const [id, n] of pairs) if (n) map[id] = Number(n);
      setDegreeCounts(map);
    });
  }, [idsRaw]);

  const removeId = (id) => {
    const remaining = ids.filter((x) => x !== id);
    if (remaining.length === 0) {
      params.delete("ids");
    } else {
      params.set("ids", remaining.join(","));
    }
    setParams(params, { replace: true });
  };

  if (!ids.length) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <GraduationCap className="h-10 w-10 mx-auto text-stone-400" />
        <h1 className="mt-4 font-editorial text-3xl">Compare FE colleges</h1>
        <p className="mt-2 text-stone-600 max-w-xl mx-auto">
          Pick two or three UK FE colleges or specialist HE providers to
          compare side-by-side — Ofsted rating, institution type, city and
          write-up in one view.
        </p>
        <Link
          to="/uk-fe-colleges"
          data-testid="fe-compare-browse-link"
          className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#4C1D95] text-white px-4 py-2 text-sm font-underground-sc tracking-wider hover:bg-[#3a1476]"
        >
          Browse UK FE colleges <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="fe-compare-page">
      <Link
        to="/uk-fe-colleges"
        className="text-sm text-stone-500 hover:text-[#4C1D95] inline-flex items-center gap-1.5"
        data-testid="fe-compare-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to all FE colleges
      </Link>

      <div className="mt-4">
        <div className="label-uppercase text-[#4C1D95]">Compare</div>
        <h1
          data-testid="fe-compare-heading"
          className="mt-1 font-editorial text-3xl sm:text-4xl text-[#002147]"
        >
          {state.unis.length ? state.unis.map((u) => u.name).join(" vs ") : "Loading…"}
        </h1>
      </div>

      {state.loading && (
        <div className="mt-8 flex items-center gap-2 text-stone-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading providers…
        </div>
      )}

      {!state.loading && state.unis.length === 0 && (
        <div
          data-testid="fe-compare-empty"
          className="mt-8 rounded-md border border-dashed border-stone-300 bg-stone-50/60 p-8 text-center text-stone-600"
        >
          None of the requested providers could be loaded.
        </div>
      )}

      {!state.loading && state.unis.length > 0 && (
        <div
          data-testid="fe-compare-grid"
          className={`mt-6 grid gap-4 grid-cols-1 ${state.unis.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}
        >
          {state.unis.map((u) => (
            <div
              key={u.id}
              data-testid={`fe-compare-card-${u.id}`}
              className="rounded-lg border border-stone-200 bg-white p-5 relative"
            >
              <button
                type="button"
                onClick={() => removeId(u.id)}
                data-testid={`fe-compare-remove-${u.id}`}
                className="absolute top-3 right-3 text-stone-400 hover:text-rose-600"
                aria-label={`Remove ${u.name}`}
              >
                <XCircle className="h-4 w-4" />
              </button>

              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                {TYPE_LABELS[u.institution_type] || "Higher education provider"}
              </div>
              <div
                data-testid={`fe-compare-name-${u.id}`}
                className="mt-1 font-editorial text-xl text-[#002147] leading-snug"
              >
                {u.name}
              </div>
              <div className="mt-0.5 text-[12px] text-stone-500">
                {u.city || "United Kingdom"}
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                <OfstedChip
                  rating={u.ofsted_rating}
                  inspectionDate={u.ofsted_inspection_date}
                />
                {!u.ofsted_rating && (
                  <span className="text-[10px] font-mono text-stone-400 uppercase tracking-wider">
                    Ofsted rating not yet indexed
                  </span>
                )}
              </div>

              {u.established && (
                <div
                  data-testid={`fe-compare-established-${u.id}`}
                  className="mt-3 text-[12px] text-stone-600"
                >
                  Established{" "}
                  <span className="font-mono font-semibold">{u.established}</span>
                </div>
              )}

              {degreeCounts[u.id] != null && (
                <div className="mt-1 text-[12px] text-stone-600">
                  HE-level courses:{" "}
                  <span className="font-mono font-semibold">
                    {degreeCounts[u.id]}
                  </span>
                </div>
              )}

              {u.description && (
                <p
                  data-testid={`fe-compare-description-${u.id}`}
                  className="mt-4 text-[13px] text-stone-700 leading-snug line-clamp-[10]"
                >
                  {u.description}
                </p>
              )}

              <div className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-between">
                <Link
                  to={`/uni/${u.id}`}
                  data-testid={`fe-compare-full-link-${u.id}`}
                  className="text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
                >
                  Full page →
                </Link>
                {u.official_domains?.[0] && (
                  <a
                    href={`https://${u.official_domains[0]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-mono text-stone-400 hover:text-stone-700"
                  >
                    {u.official_domains[0]} ↗
                  </a>
                )}
              </div>
            </div>
          ))}

          {state.unis.length < 3 && (
            <Link
              to="/uk-fe-colleges"
              data-testid="fe-compare-add-slot"
              className="rounded-lg border-2 border-dashed border-stone-300 bg-stone-50/50 p-5 flex flex-col items-center justify-center text-stone-500 hover:border-[#4C1D95] hover:text-[#4C1D95] transition-colors min-h-[240px]"
            >
              <ArrowRight className="h-5 w-5 mb-2" />
              <div className="text-[13px] font-underground-sc tracking-wider">
                Add another provider
              </div>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
